import { EqualsFilterValueType } from '../EqualsFilter';
import { FilterInterface, FilterType } from '../FilterInterface';
import { OneOfFilterValueType } from '../OneOFilter';
import { RangeFilterValueType } from '../RangeFilter';
import { RangeFilterValue } from '../RangeFilterValue';
import { FilterValueType } from './FilterEquipService';
import { separator } from './SerializationConstants';

export class FilterSerializer {
    /**
     * Serialize the value of the given Filter
     *
     * Notice: The returned string is **not** URL encoded. Encoding must be performed by the consumer
     *
     * @param {FilterInterface<T>} filter
     * @return {string}
     */
    public serializeFilterState<T extends FilterValueType>(filter: FilterInterface<T>): string | undefined {
        if (!filter.isActive) {
            return undefined;
        }
        switch (filter.type) {
            case FilterType.Equals:
                // filter = EqualsFilter<EqualsFilterValueType>
                return this.prepareEqualsFilterValue(filter.value as EqualsFilterValueType);
            case FilterType.Contains:
                // filter = ContainsFilter<EqualsFilterValueType>
                return this.prepareEqualsFilterValue(filter.value as EqualsFilterValueType);
            case FilterType.OneOf:
                // filter = OneOfFilter<OneOfFilterValueType>
                return this.prepareOneOfFilterValueType(filter.value as OneOfFilterValueType);
            case FilterType.Range:
                // filter = RangeFilter<RangeFilterValueType>
                return this.prepareRangeFilterValueType(filter.value as RangeFilterValueType);
            case FilterType.Callback:
                // filter = CallbackFilter
                return this.prepareCallbackFilterValueType(filter.value);
        }
    }

    private prepareEqualsFilterValue<T extends EqualsFilterValueType>(value: T): string | undefined {
        return this.serializeValue(value);
    }

    private prepareOneOfFilterValueType<T extends OneOfFilterValueType>(value: T): string {
        if (value.length === 0) {
            return '';
        }

        return value
            .map(v => {
                const serialized = this.serializeValue(v);

                return serialized !== undefined ? serialized : '';
            })
            .join(separator);
    }

    private prepareRangeFilterValueType<T extends RangeFilterValueType>(value: T): string | undefined {
        if (!value) {
            return undefined;
        } else {
            return this.serializeValue(value.min) + separator + this.serializeValue(value.max);
        }
    }

    private prepareCallbackFilterValueType<T>(value: T): string | undefined {
        if (value instanceof RangeFilterValue) {
            return this.prepareRangeFilterValueType(value);
        }
        if (Array.isArray(value)) {
            return this.prepareOneOfFilterValueType(value);
        }

        return this.serializeValue(value);
    }

    private serializeValue(value: any): string | undefined {
        if (value === undefined) {
            return undefined;
        } else if (value === null) {
            return '';
        } else if (typeof value === 'number') {
            return Number.isInteger(value)
                ? value.toFixed(0)
                : value.toFixed(2);
        } else if (typeof value === 'string') {
            return value;
        } else if (typeof value.toString === 'function') {
            return value.toString();
        } else {
            return undefined;
        }
    }
}
